/**
 * Performance Radar Chart
 * Pure SVG 4-axis radar/spider chart for product performance metrics.
 * No external dependencies.
 */
(function () {
  'use strict';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var VIEW_SIZE = 300;
  var CENTER = VIEW_SIZE / 2;
  var RADIUS = 110;
  var GRID_LEVELS = 5; // concentric grid rings (2, 4, 6, 8, 10)
  var MAX_VALUE = 10;
  var LABEL_OFFSET = 30;

  var AXES = [
    { key: 'cushioning', label: 'Cushioning', angle: -Math.PI / 2 },          // top
    { key: 'durability', label: 'Durability', angle: 0 },                      // right
    { key: 'moistureWicking', label: 'Moisture Wicking', angle: Math.PI / 2 }, // bottom
    { key: 'breathability', label: 'Breathability', angle: Math.PI }           // left
  ];

  function getPoint(angle, value) {
    var r = (value / MAX_VALUE) * RADIUS;
    return {
      x: CENTER + r * Math.cos(angle),
      y: CENTER + r * Math.sin(angle)
    };
  }

  function createSVGElement(tag, attrs) {
    var el = document.createElementNS(SVG_NS, tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        el.setAttribute(key, attrs[key]);
      });
    }
    return el;
  }

  function polygonPoints(values) {
    return AXES.map(function (axis, i) {
      var pt = getPoint(axis.angle, values[i]);
      return pt.x + ',' + pt.y;
    }).join(' ');
  }

  function buildChart(container) {
    var data = {
      cushioning: parseInt(container.getAttribute('data-cushioning'), 10) || 0,
      durability: parseInt(container.getAttribute('data-durability'), 10) || 0,
      breathability: parseInt(container.getAttribute('data-breathability'), 10) || 0,
      moistureWicking: parseInt(container.getAttribute('data-moisture-wicking'), 10) || 0
    };

    var values = [data.cushioning, data.durability, data.moistureWicking, data.breathability];

    var svg = createSVGElement('svg', {
      viewBox: '0 0 ' + VIEW_SIZE + ' ' + VIEW_SIZE,
      'aria-hidden': 'true',
      focusable: 'false',
      role: 'presentation'
    });

    // -- Background grid (concentric polygons) --
    for (var level = 1; level <= GRID_LEVELS; level++) {
      var gridValue = (level / GRID_LEVELS) * MAX_VALUE;
      var gridPts = AXES.map(function (axis) {
        var pt = getPoint(axis.angle, gridValue);
        return pt.x + ',' + pt.y;
      }).join(' ');

      svg.appendChild(createSVGElement('polygon', {
        points: gridPts,
        class: 'perf-chart__grid-line',
        fill: 'none'
      }));
    }

    // -- Axis lines --
    AXES.forEach(function (axis) {
      var endPt = getPoint(axis.angle, MAX_VALUE);
      svg.appendChild(createSVGElement('line', {
        x1: CENTER,
        y1: CENTER,
        x2: endPt.x,
        y2: endPt.y,
        class: 'perf-chart__axis'
      }));
    });

    // -- Data fill polygon (separate for fade animation) --
    var fillPolygon = createSVGElement('polygon', {
      points: polygonPoints(values),
      class: 'perf-chart__data-fill',
      fill: 'rgba(45, 106, 79, 0.18)',
      stroke: 'none'
    });
    svg.appendChild(fillPolygon);

    // -- Data stroke polygon (draw animation) --
    var strokePolygon = createSVGElement('polygon', {
      points: polygonPoints(values),
      class: 'perf-chart__data-polygon perf-chart__data-polygon--draw',
      fill: 'none'
    });
    svg.appendChild(strokePolygon);

    // -- Data points + value labels (per axis) --
    AXES.forEach(function (axis, i) {
      var val = values[i];
      var pt = getPoint(axis.angle, val);

      var group = createSVGElement('g', {
        class: 'perf-chart__axis-group'
      });

      // Invisible larger hit area for hover
      group.appendChild(createSVGElement('circle', {
        cx: pt.x,
        cy: pt.y,
        r: 16,
        fill: 'transparent',
        class: 'perf-chart__hit-area',
        style: 'cursor:pointer'
      }));

      // Visible data point
      var point = createSVGElement('circle', {
        cx: pt.x,
        cy: pt.y,
        class: 'perf-chart__data-point perf-chart__data-point--animated'
      });
      group.appendChild(point);

      // Value label (shown on hover via CSS)
      var valueLabelOffset = val >= 8 ? -14 : 14;
      var vlY = pt.y + valueLabelOffset;

      // Clamp label within viewbox
      if (vlY < 14) vlY = pt.y + 14;
      if (vlY > VIEW_SIZE - 6) vlY = pt.y - 14;

      var valueLabel = createSVGElement('text', {
        x: pt.x,
        y: vlY,
        class: 'perf-chart__value-label'
      });
      valueLabel.textContent = val + '/10';
      group.appendChild(valueLabel);

      svg.appendChild(group);
    });

    // -- Axis labels --
    AXES.forEach(function (axis) {
      var labelPt = getPoint(axis.angle, MAX_VALUE);
      var lx = labelPt.x;
      var ly = labelPt.y;

      // Offset labels outward from their axis endpoint
      var cos = Math.cos(axis.angle);
      var sin = Math.sin(axis.angle);
      lx += cos * LABEL_OFFSET;
      ly += sin * LABEL_OFFSET;

      // Fine-tune vertical alignment
      var dy = '0.35em';
      if (Math.abs(sin) > 0.9) {
        // Top or bottom axis
        ly += sin > 0 ? 6 : -6;
      }

      var label = createSVGElement('text', {
        x: lx,
        y: ly,
        dy: dy,
        class: 'perf-chart__label'
      });

      // Multi-line for "Moisture Wicking"
      if (axis.label.indexOf(' ') !== -1 && axis.label.length > 12) {
        var parts = axis.label.split(' ');
        var mid = Math.ceil(parts.length / 2);
        var line1 = parts.slice(0, mid).join(' ');
        var line2 = parts.slice(mid).join(' ');

        var tspan1 = createSVGElement('tspan', { x: lx, dy: '-0.4em' });
        tspan1.textContent = line1;
        var tspan2 = createSVGElement('tspan', { x: lx, dy: '1.2em' });
        tspan2.textContent = line2;
        label.appendChild(tspan1);
        label.appendChild(tspan2);
      } else {
        label.textContent = axis.label;
      }

      svg.appendChild(label);
    });

    // -- Scale markers on one axis (top axis for reference) --
    [5, 10].forEach(function (scaleVal) {
      var scalePt = getPoint(AXES[0].angle, scaleVal);
      var scaleLabel = createSVGElement('text', {
        x: scalePt.x + 10,
        y: scalePt.y + 3,
        class: 'perf-chart__scale-value'
      });
      scaleLabel.textContent = scaleVal;
      svg.appendChild(scaleLabel);
    });

    container.appendChild(svg);

    // -- Tooltip on hover --
    var tooltip = document.createElement('div');
    tooltip.className = 'perf-chart__tooltip';
    container.parentElement.style.position = 'relative';
    container.parentElement.appendChild(tooltip);

    var axisGroups = container.querySelectorAll('.perf-chart__axis-group');
    axisGroups.forEach(function (group, i) {
      group.addEventListener('mouseenter', function () {
        var label = AXES[i].label;
        var val = values[i];
        tooltip.textContent = label + ': ' + val + '/10';
        tooltip.classList.add('perf-chart__tooltip--visible');

        // Position tooltip near the data point
        var rect = container.getBoundingClientRect();
        var pt = getPoint(AXES[i].angle, values[i]);
        var scaleX = rect.width / VIEW_SIZE;
        var scaleY = rect.height / VIEW_SIZE;
        tooltip.style.left = (pt.x * scaleX - tooltip.offsetWidth / 2) + 'px';
        tooltip.style.top = (pt.y * scaleY - tooltip.offsetHeight - 10) + 'px';
      });

      group.addEventListener('mouseleave', function () {
        tooltip.classList.remove('perf-chart__tooltip--visible');
      });
    });
  }

  // -- Initialize all chart containers --
  function init() {
    var charts = document.querySelectorAll('.performance-chart');
    charts.forEach(function (chart) {
      if (chart.children.length === 0) {
        // Use IntersectionObserver for lazy animation trigger
        if ('IntersectionObserver' in window) {
          var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                buildChart(entry.target);
                observer.unobserve(entry.target);
              }
            });
          }, { threshold: 0.3 });
          observer.observe(chart);
        } else {
          buildChart(chart);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
